// A playable match: the engine, an AI, and the bookkeeping that turns "the
// engine is waiting on player 1" into "the AI thinks for a moment and moves".
//
// The view layer never touches the engine directly. It asks the session what is
// legal, submits an action, and reads the event log to animate. That keeps the
// rules honest -- a UI bug can make the board look wrong, but it cannot make an
// illegal play.

import { chooseAction, type AiTrace, type Difficulty } from '../ai/opponent.ts';
import { MatchLog, type Replay } from './log.ts';
import { buildContext, buildDeck, legalLegends, type BuildContext } from '../rules/deck.ts';
import { advance, applyAction, createGame, legalActions, type DeckList } from '../rules/engine.ts';
import type { LoadedRules } from '../rules/load.ts';
import { seedFrom } from '../rules/rng.ts';
import type { Action, GameEvent, GameState, ObjectId, PlayerId } from '../rules/types.ts';
import type { Arrangement } from './layout.ts';

export interface SessionOptions {
  /** Seed string; the same one always produces the same match. */
  seed?: string;
  difficulty?: Difficulty;
  /**
   * The legend the AI builds around, for practising against one archetype.
   *
   * Applied *after* both seeded decks are built, so asking for a particular
   * opponent does not shift the player's own deck or the shuffle -- the same
   * reason `humanDeck` is applied that way.
   */
  aiLegend?: string;
  /** Which seat the human plays. */
  human?: PlayerId;
  /** Milliseconds the AI appears to "think", so its turn is followable. */
  aiDelayMs?: number;
  /**
   * The human's deck, when they chose one.
   *
   * Left out, both decks are built from the seed, which is what a quick game
   * and every headless harness wants. Given, only the *opponent's* deck still
   * comes from the seed -- so a chosen deck does not change what the AI is
   * holding, and two players trying the same seed meet the same opponent.
   */
  humanDeck?: DeckList;
}

/**
 * The action the match's main button should take, given what the engine will
 * accept right now.
 *
 * Derived from the legal actions rather than from the shape of the board. The
 * HUD used to read `state.combat` and assume "pass", but while combat damage is
 * waiting to be assigned the engine offers only `assignDamage` and no pass at
 * all -- so the button was enabled, did nothing, and the match could not go on.
 */
export function primaryActionFrom(actions: readonly Action[]): Action | null {
  // Keeping the hand is the mulligan state's "do nothing and move on".
  const keep = actions.find((a) => a.t === 'mulligan' && a.cards.length === 0);
  return keep
    ?? actions.find((a) => a.t === 'assignDamage')
    ?? actions.find((a) => a.t === 'endTurn')
    ?? actions.find((a) => a.t === 'pass')
    // 355.3 / 424. A pending choice is answered through its overlay, but the
    // button must never be the reason a match cannot go on: the engine offers
    // its answers best-first, so the first one is a defensible default.
    ?? actions.find((a) => a.t === 'choose')
    ?? null;
}

/**
 * The two decks a seed deals, and the seed the game is then created from.
 *
 * Pulled out of `Session`'s constructor because a replay has to derive exactly
 * the same pair: a replay stores "seed and actions, nothing else", so watching
 * one means rebuilding the decks the same way down to the order the legends
 * were drawn in. A second implementation of this would be a second opinion
 * about what a seed means, and the two would disagree silently -- the actions
 * would still apply, mostly, against a different set of cards.
 */
export function decksFor(
  rules: LoadedRules, ctx: BuildContext, seedText: string,
  opts: { human?: PlayerId; humanDeck?: DeckList; aiLegend?: string } = {},
): { decks: [DeckList, DeckList]; startSeed: number; custom: boolean } {
  const human = opts.human ?? 0;
  const ai: PlayerId = human === 0 ? 1 : 0;
  const seed = seedFrom(seedText);
  const rng = { seed };

  const legends = legalLegends(ctx);
  if (legends.length < 2) throw new Error('not enough legends to build two decks');

  const pick = () => legends[Math.floor((rng.seed = (rng.seed * 1664525 + 1013904223) >>> 0)
    / 4294967296 * legends.length)] ?? legends[0]!;

  // Both are drawn from the seed even when one is then replaced, so the
  // opponent's deck does not shift depending on what the player picked.
  const decks: [DeckList, DeckList] = [
    buildDeck(ctx, rng, pick(), { scripts: rules.scripts, preferModelled: true }),
    buildDeck(ctx, rng, pick(), { scripts: rules.scripts, preferModelled: true }),
  ];
  if (opts.humanDeck) decks[human] = opts.humanDeck;
  if (opts.aiLegend && ctx.index.has(opts.aiLegend)) {
    // Its own stream, derived from the match seed: drawing from `rng` here
    // would change every shuffle after it, so the same seed would deal a
    // different game depending on who you chose to practise against.
    const aiRng = { seed: (seed ^ 0x2545f491) >>> 0 };
    decks[ai] = buildDeck(ctx, aiRng, ctx.index.get(opts.aiLegend),
      { scripts: rules.scripts, preferModelled: true });
  }

  // A match whose decks were chosen rather than dealt cannot be rebuilt from
  // the seed alone, so the replay carries them. Ordinary matches record nothing
  // extra and keep the "seed and actions, nothing else" property.
  return { decks, startSeed: rng.seed, custom: !!opts.humanDeck || !!opts.aiLegend };
}

export class Session {
  readonly rules: LoadedRules;
  readonly ctx: BuildContext;
  readonly human: PlayerId;
  readonly ai: PlayerId;
  readonly decks: [DeckList, DeckList];
  /** Every action taken, why, and what followed -- and the replay it doubles as. */
  readonly log: MatchLog;

  state: GameState;

  private readonly difficulty: Difficulty;
  private readonly customDecks: boolean = false;
  /** Kept verbatim: the replay reproduces the match from this one string. */
  private readonly seedText: string;
  /** The numeric seed the game was dealt from, for rewinding to it. */
  private readonly startSeed: number;
  private readonly aiRng: { seed: number };
  /**
   * Milliseconds the AI pauses before acting. Settable, because it is a player
   * setting: 0 means no delay at all, and the scheduler then stops using a
   * timer, which is also what an unattended tab needs.
   */
  aiDelayMs: number;
  private cancelAi: (() => void) | null = null;
  private eventCursor = 0;

  /** Called after any state change, so the view can rebuild its layout. */
  onChange: (() => void) | null = null;
  /** Called with events the view has not animated yet. */
  onEvents: ((events: GameEvent[]) => void) | null = null;

  constructor(rules: LoadedRules, opts: SessionOptions = {}) {
    this.rules = rules;
    this.ctx = buildContext(rules.index, rules.available);
    this.human = opts.human ?? 0;
    this.ai = this.human === 0 ? 1 : 0;
    this.difficulty = opts.difficulty ?? 'normal';
    this.aiDelayMs = opts.aiDelayMs ?? 550;

    this.seedText = opts.seed ?? `match-${Date.now()}`;
    this.aiRng = { seed: seedFrom(this.seedText) ^ 0x5bf03635 };

    const dealt = decksFor(rules, this.ctx, this.seedText, {
      human: this.human,
      ...(opts.humanDeck ? { humanDeck: opts.humanDeck } : {}),
      ...(opts.aiLegend ? { aiLegend: opts.aiLegend } : {}),
    });
    this.decks = dealt.decks;
    this.customDecks = dealt.custom;
    this.startSeed = dealt.startSeed;
    this.state = createGame(rules, this.decks, this.startSeed);
    this.log = new MatchLog(rules, this.human);
    advance(this.state, rules);
    this.settle();
  }

  // -- queries the view uses --------------------------------------------------

  get waitingOnHuman(): boolean {
    const w = this.state.waiting;
    return 'player' in w && w.player === this.human;
  }

  get isOver(): boolean {
    return this.state.waiting.kind === 'gameOver';
  }

  /** Actions the human may take right now; empty when it is not their turn. */
  humanActions(): Action[] {
    if (!this.waitingOnHuman) return [];
    return legalActions(this.state, this.rules);
  }

  /** Events the view has not seen yet, marking them consumed. */
  drainEvents(): GameEvent[] {
    const fresh = this.state.events.slice(this.eventCursor);
    this.eventCursor = this.state.events.length;
    return fresh;
  }

  // -- driving the match ------------------------------------------------------

  submit(action: Action): boolean {
    if (!this.waitingOnHuman) return false;
    const before = this.state.events.length;
    if (!applyAction(this.state, this.rules, action)) return false;
    advance(this.state, this.rules);
    this.log.record(this.state, this.human, action, this.state.events.slice(before));
    this.settle();
    return true;
  }

  /**
   * Takes back the player's last move, and everything the AI did after it.
   *
   * There is no undo in the engine and there should not be: an engine that can
   * go backwards is one that has to keep every intermediate state. What there
   * is instead is determinism -- the same seed and the same actions always
   * reach the same position -- so a rewind is a *replay* of the match minus its
   * tail, which lands on exactly the state that existed before the move.
   *
   * The AI's replies come off with it. Undoing your move but leaving the
   * answer to it standing would put the board in a position that never
   * happened, and the AI re-decides from the restored state anyway.
   *
   * Returns whether anything was taken back.
   */
  readonly canRewind = true;

  /**
   * Where the player has arranged their base.
   *
   * Local only, and that is the whole of it here: the AI has no view to keep in
   * step with. It still lives on the match rather than in the board so both
   * kinds of match answer the same question the same way.
   */
  readonly arrangement: Arrangement = {};

  arrange(object: ObjectId, cell: [number, number]): void {
    this.arrangement[object] = cell;
    this.onChange?.();
  }

  rewind(): boolean {
    const actions = this.log.actions;
    // Walk back to the player's own last action; that is what "undo" means to
    // the person asking, not "undo whatever happened most recently".
    let cut = -1;
    for (let i = actions.length - 1; i >= 0; i--) {
      if (this.actorOf(i) === this.human) { cut = i; break; }
    }
    if (cut < 0) return false;

    const keep = actions.slice(0, cut);
    this.cancelAi?.();
    this.cancelAi = null;

    this.state = createGame(this.rules, this.decks, this.startSeed);
    advance(this.state, this.rules);
    for (const action of keep) {
      if (!applyAction(this.state, this.rules, action)) break;
      advance(this.state, this.rules);
    }

    this.log.rewind(actions.length - cut);
    // Everything before the rewind has been re-announced by the replay, so the
    // view must not animate it a second time.
    this.eventCursor = this.state.events.length;
    this.settle();
    return true;
  }

  /** Which side took the action at `i`, from the log's own record. */
  private actorOf(i: number): PlayerId {
    return this.log.entries[i]?.side === 'you' ? this.human : this.ai;
  }

  /** The match so far, as a file that can reproduce it exactly. */
  replay(): Replay {
    return this.log.replay(this.seedText, this.difficulty, this.state,
      this.customDecks ? this.decks : undefined);
  }

  /**
   * Hands control to the AI whenever the engine is waiting on it. The delay is
   * cosmetic: without it the AI's whole turn resolves inside one frame and the
   * board simply teleports.
   */
  private settle(): void {
    this.notify();
    this.cancelAi?.();
    this.cancelAi = null;
    if (this.isOver) return;

    const w = this.state.waiting;
    if (!('player' in w) || w.player !== this.ai) return;

    this.cancelAi = schedule(this.aiDelayMs, () => {
      this.cancelAi = null;
      const trace: AiTrace = {
        considered: 0, evaluated: 0, score: 0, runnerUp: -Infinity, blundered: false,
      };
      const action = chooseAction(this.state, this.rules, {
        difficulty: this.difficulty,
        rng: this.aiRng,
        // No budget passed, deliberately. `chooseAction` sets its own from the
        // difficulty -- 120ms for hard, 40 otherwise -- and passing 30 here
        // overrode it, which is why "hard" and "normal" were the same agent in
        // an actual match: hard's entire stated edge is that it can afford to
        // score every candidate, and one candidate costs about 11ms. The
        // hitch is real and it is once per AI action, on a timer rather than
        // in the frame loop.
        trace,
      });
      const before = this.state.events.length;
      if (action && applyAction(this.state, this.rules, action)) {
        advance(this.state, this.rules);
        this.log.record(this.state, this.ai, action, this.state.events.slice(before), trace);
      } else {
        // The AI could not act; concede rather than deadlock the match.
        const give: Action = { t: 'concede' };
        applyAction(this.state, this.rules, give);
        this.log.record(this.state, this.ai, give, this.state.events.slice(before));
      }
      this.settle();
    });
  }

  private notify(): void {
    const fresh = this.state.events.slice(this.eventCursor);
    if (fresh.length > 0) this.onEvents?.(fresh);
    this.eventCursor = this.state.events.length;
    this.onChange?.();
  }

  destroy(): void {
    this.cancelAi?.();
    this.cancelAi = null;
    this.onChange = null;
    this.onEvents = null;
  }
}

/**
 * Runs `fn` after `ms`, and returns a cancel.
 *
 * A delay of zero means *no* delay, and `setTimeout(fn, 0)` does not deliver
 * that: a browser clamps timers in a hidden tab to about a second, so a match
 * driven at zero delay -- a test harness, or a tab the user switched away from
 * mid-turn -- advances one AI action per second and looks hung. A whole AI turn
 * is a few hundred of those, which reads as a frozen client rather than a slow
 * one. A MessageChannel is the one task source browsers do not throttle, and
 * that is exactly what "as fast as possible" asks for. The cosmetic path keeps
 * the timer, because there the pacing is the point.
 */
function schedule(ms: number, fn: () => void): () => void {
  if (ms > 0) {
    const id = setTimeout(fn, ms);
    return () => clearTimeout(id);
  }
  const chan = new MessageChannel();
  let live = true;
  chan.port1.onmessage = () => {
    chan.port1.close();
    if (live) fn();
  };
  chan.port2.postMessage(0);
  return () => { live = false; chan.port1.close(); };
}
